import { useState, useEffect } from "react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Store, Loader2, Sparkles, ExternalLink, Check } from "lucide-react";

export default function Marketplace() {
  const [partners, setPartners] = useState([]);
  const [cat, setCat] = useState("All");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => { api.get("/marketplace/partners").then((r) => setPartners(r.data)).finally(() => setLoading(false)); }, []);
  const featured = partners.filter((p) => p.featured);
  const rest = partners.filter((p) => !p.featured);
  const cats = ["All", ...Array.from(new Set(rest.map((p) => p.category)))];
  const shown = cat === "All" ? rest : rest.filter((p) => p.category === cat);

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-xd-blue/20 text-xd-blue"><Store size={22} /></span>
          <div><h1 className="font-heading text-4xl font-black tracking-tight">XD Freight Marketplace</h1><p className="text-xd-muted">Products and services for the businesses that keep freight moving.</p></div>
        </div>

        {/* Featured partners */}
        {featured.map((p) => (
          <div key={p.id} data-testid={`featured-${p.id}`} className="mt-8 rounded-2xl border border-xd-gold/40 bg-gradient-to-r from-xd-gold/10 via-xd-paper to-xd-paper p-6 sm:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <span className="grid place-items-center w-16 h-16 rounded-2xl bg-xd-gold text-xd-bg font-heading font-black text-2xl shrink-0">{p.company[0]}</span>
              <div className="flex-1 min-w-0">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-xd-gold"><Sparkles size={13} /> Featured Partner · {p.category}</span>
                <h2 className="mt-1 font-heading text-2xl font-black">{p.company}</h2>
                <p className="mt-2 text-sm text-xd-muted max-w-2xl">{p.description}</p>
              </div>
              <div className="flex flex-col sm:flex-row lg:flex-col gap-2 shrink-0">
                <Button data-testid={`featured-explore-${p.id}`} onClick={() => setDetail(p)} className="bg-xd-blue hover:bg-xd-blueH text-white font-semibold rounded-xl">{p.cta_text || "Explore Services"}</Button>
                <Button variant="outline" className="border-xd-border text-white hover:bg-xd-surface rounded-xl" onClick={() => window.open(p.website, "_blank", "noopener")}>Visit Partner <ExternalLink size={14} className="ml-2" /></Button>
              </div>
            </div>
          </div>
        ))}

        {/* Category filter */}
        <div className="mt-8 flex flex-wrap gap-2">
          {cats.map((c) => (
            <button key={c} data-testid={`mp-cat-${c}`} onClick={() => setCat(c)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${cat === c ? "bg-xd-blue border-xd-blue text-white" : "border-xd-border text-xd-muted hover:text-white"}`}>{c}</button>
          ))}
        </div>

        {loading ? <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-xd-blue" size={32} /></div> : (
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {shown.map((p) => (
              <div key={p.id} data-testid={`partner-${p.id}`} className="bg-xd-paper border border-xd-border rounded-xl p-6 hover:border-xd-blue/50 transition-colors flex flex-col">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-11 h-11 rounded-lg bg-xd-surface border border-xd-border text-xd-blue font-heading font-black">{p.company[0]}</span>
                  <div><h3 className="font-heading font-bold leading-tight">{p.company}</h3><p className="text-xs text-xd-dim uppercase tracking-wide">{p.category}</p></div>
                </div>
                <p className="mt-4 text-sm text-xd-muted flex-1">{p.description}</p>
                <div className="mt-4 rounded-lg bg-xd-surface border border-xd-border p-3">
                  <p className="text-xs text-xd-dim">Offer</p>
                  <p className="text-sm font-semibold text-xd-green">{p.offer}</p>
                  <p className="mt-1 text-xs text-xd-dim">Provider: {p.company}</p>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" className="flex-1 border-xd-border text-white hover:bg-xd-surface rounded-xl" size="sm" onClick={() => setDetail(p)}>Learn More</Button>
                  <Button className="flex-1 bg-xd-blue hover:bg-xd-blueH text-white rounded-xl" size="sm" onClick={() => p.website ? window.open(p.website, "_blank", "noopener") : setDetail(p)}>
                    {p.website ? <>Visit Site <ExternalLink size={13} className="ml-1.5" /></> : "Apply"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-xd-paper border-xd-border text-white max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-2xl">{detail.company}</DialogTitle></DialogHeader>
              <p className="text-xs uppercase tracking-wide text-xd-gold font-bold">{detail.category}</p>
              <p className="text-sm text-xd-muted">{detail.description}</p>
              {detail.who_for && <p className="text-sm"><span className="text-xd-dim">Who it's for: </span>{detail.who_for}</p>}
              {detail.services?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-xd-dim font-bold mb-2">Services</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {detail.services.map((s) => <span key={s} className="flex items-center gap-2 text-sm text-xd-muted"><Check size={14} className="text-xd-green shrink-0" /> {s}</span>)}
                  </div>
                </div>
              )}
              {detail.disclosure && <p className="rounded-lg border border-xd-border bg-xd-surface p-3 text-xs text-xd-dim">{detail.disclosure}</p>}
              {detail.website ? (
                <Button data-testid="visit-partner-btn" onClick={() => window.open(detail.website, "_blank", "noopener")} className="bg-xd-gold hover:bg-xd-goldH text-xd-bg font-bold rounded-xl">Visit {detail.company} <ExternalLink size={15} className="ml-2" /></Button>
              ) : (
                <Button className="bg-xd-blue hover:bg-xd-blueH text-white rounded-xl">Request Info</Button>
              )}
              <p className="text-[11px] text-xd-dim">Marketplace services are provided by third-party partners and are separate from transportation services provided by XD Freight LLC.</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PublicLayout>
  );
}
